import { c as _c } from "react/compiler-runtime";
import { IcoForbid40 } from '@onefootprint/icons';
import type { Entity } from '@onefootprint/request-types/dashboard';
import { Divider, MultiSelect } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { type JSX, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type DeviceInsightEvent, type DeviceVisitEvent, isDeviceVisit } from '@/entities/components/details/hooks/use-entity-device-insights';
import useSession from '@/hooks/use-session';
import MapComponent from '../map';
import MapMarker from '../map/components/map-marker';
import AddressCard from './components/address-card';
import AddressType from './components/address-card/types';
import ContainerWithToggle from './components/container-with-toggle';
import DeviceVisitCard from './components/device-visit-card';
import EmployeeOnlyIcon from './components/employee-only-icon';
import FingerprintAnnouncement from './components/fingerprint-announcement';
import InsightEventCard from './components/insight-event-card';
import useEntries from './hooks/use-entries';
import useMultiSelectOptions, { type MultiSelectOptionValue } from './hooks/use-multi-select-options';
const isSuperseded = (e: DeviceInsightEvent | DeviceVisitEvent) => e.event.kind === 'device_insight' && e.event.data.isSuperseded;
export type ContentProps = {
  entity: Entity;
  events: (DeviceInsightEvent | DeviceVisitEvent)[];
};
const Content = t0 => {
  const $ = _c(68);
  const {
    entity,
    events
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "deviceInsights"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  const {
    data: session
  } = useSession();
  let t2;
  if ($[1] !== events) {
    t2 = events.some(_temp);
    $[1] = events;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const isFingerprint = t2;
  let t3;
  let visibleEvents;
  if ($[3] !== events || $[4] !== session.user?.isFirmEmployee) {
    visibleEvents = session.user?.isFirmEmployee ? events : events.filter(_temp2);
    t3 = new Set(visibleEvents.flatMap(_temp3));
    $[3] = events;
    $[4] = session.user?.isFirmEmployee;
    $[5] = t3;
    $[6] = visibleEvents;
  } else {
    t3 = $[5];
    visibleEvents = $[6];
  }
  const supersededKinds = t3;
  const [detailsHidden, setDetailsHidden] = useState(false);
  const {
    allOptions,
    selectedOptions,
    handleOptionsChange
  } = useMultiSelectOptions(entity, visibleEvents);
  const {
    entries,
    lines,
    selectedCoords,
    selectedId,
    onSelectedIdChange
  } = useEntries(entity, visibleEvents, selectedOptions);
  const selectedCardId = selectedId && entries[selectedId]?.cardId || selectedId;
  let t4;
  if ($[7] !== allOptions || $[8] !== supersededKinds) {
    let t5;
    if ($[10] !== supersededKinds) {
      t5 = option => supersededKinds.has(option.value) ? {
        ...option,
        suffix: <EmployeeOnlyIcon />
      } : option;
      $[10] = supersededKinds;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    t4 = allOptions.map(t5);
    $[7] = allOptions;
    $[8] = supersededKinds;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const options = t4;
  let cards;
  let markers;
  if ($[12] !== entity || $[13] !== entries || $[14] !== onSelectedIdChange || $[15] !== selectedCardId || $[16] !== selectedId || $[17] !== t) {
    cards = [];
    markers = [];
    Object.entries(entries).forEach(t5 => {
      const [id, entry] = t5;
      const isSelected = selectedCardId === id;
      if (entry.type === "businessAddress" || entry.type === "residentialAddress") {
        const type = entry.type === "businessAddress" ? AddressType.business : AddressType.residential;
        cards.push(<AddressCard id={id} key={id} type={type} entity={entity} isPending={entry.cardisPending} isSelected={isSelected} onSelect={onSelectedIdChange} />);
      } else {
        if (entry.data && isDeviceVisit(entry.data)) {
          cards.push(<DeviceVisitCard id={id} key={id} data={entry.data} isSelected={isSelected} onSelect={onSelectedIdChange} />);
        } else {
          if (entry.data) {
            cards.push(<InsightEventCard id={id} key={id} data={entry.data} isSelected={isSelected} onSelect={onSelectedIdChange} isEmployeeOnly={entry.data.event.data.isSuperseded} />);
          }
        }
      }
      if (entry.marker) {
        markers.push(<MapMarker key={id} id={id} lat={entry.marker.lat} lng={entry.marker.lng} getIcon={entry.marker.getIcon} isSelected={selectedId === id} />);
      }
    });
    if (!cards.length) {
      let t6;
      if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = <IcoForbid40 />;
        $[20] = t6;
      } else {
        t6 = $[20];
      }
      let t7;
      if ($[21] !== t) {
        t7 = t(_temp4);
        $[21] = t;
        $[22] = t7;
      } else {
        t7 = $[22];
      }
      let t8;
      if ($[23] !== t7) {
        t8 = <span className="text-label-2 text-primary">{t7}</span>;
        $[23] = t7;
        $[24] = t8;
      } else {
        t8 = $[24];
      }
      let t9;
      if ($[25] !== t) {
        t9 = t(_temp5);
        $[25] = t;
        $[26] = t9;
      } else {
        t9 = $[26];
      }
      let t10;
      if ($[27] !== t9) {
        t10 = <span className="text-body-2 text-primary text-center">{t9}</span>;
        $[27] = t9;
        $[28] = t10;
      } else {
        t10 = $[28];
      }
      let t11;
      if ($[29] !== t10 || $[30] !== t8) {
        t11 = <div key="empty" className="flex flex-col gap-5 justify-center items-center p-4 grow w-full h-full">{t6}<div className="flex flex-col gap-2 justify-center items-center w-full">{t8}{t10}</div></div>;
        $[29] = t10;
        $[30] = t8;
        $[31] = t11;
      } else {
        t11 = $[31];
      }
      cards.push(t11);
    }
    $[12] = entity;
    $[13] = entries;
    $[14] = onSelectedIdChange;
    $[15] = selectedCardId;
    $[16] = selectedId;
    $[17] = t;
    $[18] = cards;
    $[19] = markers;
  } else {
    cards = $[18];
    markers = $[19];
  }
  let t5;
  if ($[32] !== entries || $[33] !== onSelectedIdChange) {
    t5 = id_0 => {
      onSelectedIdChange(id_0);
      const card = document.getElementById(`device-insights-card-${entries[id_0]?.cardId ?? id_0}`);
      const parent = card?.parentElement;
      if (card && parent) {
        parent.scrollTo({
          top: card.offsetTop,
          behavior: "smooth"
        });
      }
    };
    $[32] = entries;
    $[33] = onSelectedIdChange;
    $[34] = t5;
  } else {
    t5 = $[34];
  }
  const handleSelectFromMap = t5;
  let t6;
  if ($[35] !== t) {
    t6 = t(_temp6);
    $[35] = t;
    $[36] = t6;
  } else {
    t6 = $[36];
  }
  let t7;
  if ($[37] !== selectedOptions) {
    t7 = selectedOptions.map(_temp7);
    $[37] = selectedOptions;
    $[38] = t7;
  } else {
    t7 = $[38];
  }
  let t8;
  if ($[39] !== handleOptionsChange || $[40] !== options || $[41] !== t6 || $[42] !== t7) {
    t8 = <MultiSelect placeholder={t6} options={options} value={t7} onChange={handleOptionsChange} />;
    $[39] = handleOptionsChange;
    $[40] = options;
    $[41] = t6;
    $[42] = t7;
    $[43] = t8;
  } else {
    t8 = $[43];
  }
  let t9;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Divider className="mt-2" />;
    $[44] = t9;
  } else {
    t9 = $[44];
  }
  let t10;
  if ($[45] !== isFingerprint) {
    t10 = isFingerprint && <FingerprintAnnouncement />;
    $[45] = isFingerprint;
    $[46] = t10;
  } else {
    t10 = $[46];
  }
  let t11;
  if ($[47] !== cards || $[48] !== t10) {
    t11 = <div className="relative flex flex-col gap-3 h-full overflow-y-scroll box-content w-full pt-2 pb-3">{t10}{cards}</div>;
    $[47] = cards;
    $[48] = t10;
    $[49] = t11;
  } else {
    t11 = $[49];
  }
  let t12;
  if ($[50] !== detailsHidden || $[51] !== t11 || $[52] !== t8) {
    t12 = <ContainerWithToggle isHidden={detailsHidden} onChangeHidden={setDetailsHidden}>{t8}{t9}{t11}</ContainerWithToggle>;
    $[50] = detailsHidden;
    $[51] = t11;
    $[52] = t8;
    $[53] = t12;
  } else {
    t12 = $[53];
  }
  const t13 = !detailsHidden;
  let t14;
  if ($[54] !== t13) {
    t14 = cx("w-full h-56 shrink-0 rounded-md overflow-hidden relative border border-tertiary transition-transform duration-300 ease-in-out translate-x-0 md:h-full", {
      "md:translate-x-[214px]": t13
    });
    $[54] = t13;
    $[55] = t14;
  } else {
    t14 = $[55];
  }
  const t15 = !detailsHidden;
  let t16;
  if ($[56] !== handleSelectFromMap || $[57] !== lines || $[58] !== markers || $[59] !== selectedCoords) {
    t16 = <MapComponent markers={markers} lines={lines} selectedCoords={selectedCoords} onSelect={handleSelectFromMap} />;
    $[56] = handleSelectFromMap;
    $[57] = lines;
    $[58] = markers;
    $[59] = selectedCoords;
    $[60] = t16;
  } else {
    t16 = $[60];
  }
  let t17;
  if ($[61] !== t14 || $[62] !== t15 || $[63] !== t16) {
    t17 = <div className={t14} data-has-overlay={t15}>{t16}</div>;
    $[61] = t14;
    $[62] = t15;
    $[63] = t16;
    $[64] = t17;
  } else {
    t17 = $[64];
  }
  let t18;
  if ($[65] !== t12 || $[66] !== t17) {
    t18 = <div className="flex flex-col-reverse h-[640px] w-full rounded-md overflow-hidden relative border border-tertiary md:block md:h-[584px]">{t12}{t17}</div>;
    $[65] = t12;
    $[66] = t17;
    $[67] = t18;
  } else {
    t18 = $[67];
  }
  return t18;
};
export default Content;
function _temp(e) {
  return isDeviceVisit(e) || isSuperseded(e);
}
function _temp2(e_0) {
  return !isSuperseded(e_0);
}
function _temp3(t0) {
  const {
    event
  } = t0;
  return event.kind === "device_insight" && event.data.isSuperseded ? [event.data.kind] : [];
}
function _temp4($) {
  return $.empty.title;
}
function _temp5($_0) {
  return $_0.empty.description;
}
function _temp6($_1) {
  return $_1.select.placeholder;
}
function _temp7(option_0) {
  return option_0.value;
}